import { useEffect, useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { toast } from "sonner";
import { Sparkle } from "@phosphor-icons/react";

/** New course: pick a topic (or custom), add specifics, set pass mark & validity → AI writes modules + quiz. */
export default function CourseCreateDialog({ open, onOpenChange, onCreated }) {
  const [topics, setTopics] = useState([]);
  const [f, setF] = useState({ topic_key: "", topic: "", brief: "", audience: "", pass_mark: 80, validity_months: 12 });
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setF({ topic_key: "", topic: "", brief: "", audience: "", pass_mark: 80, validity_months: 12 }); API.get("/hse/training/topics").then(({ data }) => setTopics(data.topics || [])).catch(() => {}); } }, [open]);
  const pick = (t) => setF(x => ({ ...x, topic_key: t.key, topic: t.key === "custom" ? "" : t.label, validity_months: t.validity_months }));

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await API.post("/hse/training/courses", f);
      toast.success("The AI is writing the course…");
      onCreated?.(data); onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="course-create-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Chivo]">New training course</DialogTitle>
          <p className="text-xs text-muted-foreground">Short modules operatives read on their phone, then a quiz. A pass issues a certificate and goes straight onto the competency matrix.</p>
        </DialogHeader>
        <div className="flex flex-wrap gap-1.5">
          {topics.map(t => <button key={t.key} type="button" onClick={() => pick(t)} className={`text-[11px] px-2.5 py-1.5 rounded-full border transition-colors ${f.topic_key === t.key ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`} data-testid={`course-topic-${t.key}`}>{t.label}</button>)}
        </div>
        {f.topic_key && (
          <div className="grid gap-3 sm:grid-cols-2" data-testid="course-create-form">
            <div className="sm:col-span-2 space-y-1.5"><Label className="text-xs">Course title {f.topic_key === "custom" && "*"}</Label><Input value={f.topic} onChange={e => { const chip = topics.find(t => t.key === f.topic_key); setF({ ...f, topic: e.target.value, topic_key: chip && chip.key !== "custom" && e.target.value.trim() !== chip.label ? "custom" : f.topic_key }); }} placeholder="e.g. Safe use of podium steps" className="h-9" data-testid="course-topic-input" /></div>
            <div className="sm:col-span-2 space-y-1.5"><Label className="text-xs">Anything specific to include</Label><Textarea value={f.brief} onChange={e => setF({ ...f, brief: e.target.value })} rows={3} placeholder="Recent incidents, client rules, equipment you use…" data-testid="course-brief" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Audience</Label><Input value={f.audience} onChange={e => setF({ ...f, audience: e.target.value })} placeholder="Field operatives and supervisors" className="h-9" data-testid="course-audience" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Pass mark %</Label><Input type="number" min={50} max={100} value={f.pass_mark} onChange={e => setF({ ...f, pass_mark: Number(e.target.value) })} className="h-9" data-testid="course-pass-mark" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Valid for (months)</Label><Input type="number" min={0} max={120} value={f.validity_months} onChange={e => setF({ ...f, validity_months: Number(e.target.value) })} className="h-9" data-testid="course-validity" /></div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={create} disabled={busy || !f.topic_key || !f.topic.trim()} data-testid="course-create-btn"><Sparkle size={14} className="mr-1" /> {busy ? "Starting…" : "Write with AI"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
